/**
 * Auth_Service.ts
 * API functions for Platform auth endpoints (SK Voice Console).
 *
 * The platform owns login/signup UI; this app only needs the session probe
 * (/me), logout, and the verify-email token exchange for boot. Cookies
 * (accessToken + refreshToken) are httpOnly — never touched here.
 * Mirrors sk-catalog-frontend's auth service, scoped to product 'voice'.
 */

import axiosInstance from '../../api/axiosInstance';
import { Token_Handler } from '../../storage/tokenHandler';
import {
  Platform_Me_Url,
  Platform_Me_Setup_Url,
  Platform_Logout_Url,
  Platform_Verify_Email_Url,
} from '../../api/endpoints';

// The platform mints product-scoped JWTs; the Voice Console always authenticates
// against the 'voice' product so the issued token carries voice scopes.
export const PRODUCT = 'voice' as const;

/* ─────────────────────────────────────────
   Me — GET /v1/me
   Module-level cache (5s TTL) + in-flight deduplicator so parallel callers
   (SessionContext, RootNavigator) share one request.
───────────────────────────────────────── */

const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;

export const invalidateMeCache = () => { _meCache = null; };

// Persist onboarding progress on the org (platform metadata.setup). Invalidates
// the me-cache so the next /v1/me reflects the new setup state.
export const patchOrgSetup = async (
  body: { step?: { key: string; state: string }; completed?: boolean },
): Promise<{ completed_at: string | null; steps: Record<string, string> }> => {
  const r = await axiosInstance.patch(Platform_Me_Setup_Url, body);
  invalidateMeCache();
  return r.data?.data ?? r.data;
};

export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then((r) => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/* ─────────────────────────────────────────
   Logout — POST /v1/auth/logout
───────────────────────────────────────── */

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};

/* ─────────────────────────────────────────
   Verify Email — POST /v1/auth/verify-email
   Token arrives via the emailed link; flips the org ACTIVE.
───────────────────────────────────────── */

export interface VerifyEmailResponse {
  data: { verified: boolean; user_id: string; activated_org_ids: string[] };
}

export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  const { data } = await axiosInstance.post<VerifyEmailResponse>(Platform_Verify_Email_Url, { token });
  invalidateMeCache();
  return data;
};
